import * as React from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { Button, Stack, AbsoluteBox, Box, Backdrop, Typography, Grid, MuiBox, color } from '@kis-technologies/kis-ui';
import { Progress } from 'components/Progress';
import { useTimer } from 'hooks/useTimer';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { actions as ticketingActions } from 'services/ticketing';
import { useStyles } from '../styled';
import { TimerOverlayProps } from '../types';

/**
 * Overlay used for time left before timing out
 */
export const TimerOverlay = (props: TimerOverlayProps) => {
  const { t, i18n } = useTranslation();
  const { onResetClick, onActiveClick, startTime, flow } = props;
  const time = useTimer(onResetClick, { startTime });
  const setTimerOverlay = useActions(ticketingActions.setTimerOverlay);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const messages = get(config, 'configuration.globalSettings.inactivityTimeout.abandonSession.messages');
  const header = get(messages, `header.${i18n.language}`, t('timer.stillThere'));
  const subheader = get(messages, `subheader.${i18n.language}`, t('timer.sessionTimeout'));
  const resetButtonText = get(messages, `buttons.${i18n.language}[0]`, t('commonText.startOver'));
  const stayHereButtonText = get(messages, `buttons.${i18n.language}[1]`, t('timer.imHere'));
  const palette = get(config, 'configuration.globalSettings.palette');
  const classes = useStyles({ flow, palette })();

  React.useEffect(() => {
    setTimerOverlay(true);

    // return () => setTimerOverlay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatText = (msg: string) => {
    if (flow === 'fair') {
      return msg.toUpperCase();
    }
    return msg;
  };

  const onEl = (el: HTMLElement) => {
    const { style } = el;
    style.background = '#EFEFEF';
  };

  const resetButton = () => {
    return (
      <Button
        customSize={'x-large'}
        customColor={(flow === 'fair' && palette?.secondaryText) || color.BLACK}
        onClick={() => {
          setTimerOverlay(false);
          onResetClick();
        }}
        appearance={flow === 'fair' ? 'secondary' : 'white'}
        className={classes.timerResetButton}
        data-testid="resetButton"
      >
        {formatText(resetButtonText)}
      </Button>
    );
  };

  const stayHereButton = () => {
    return (
      <Button
        customSize="x-large"
        onClick={() => {
          setTimerOverlay(false);
          onActiveClick();
        }}
        appearance="primary"
        customColor={(flow === 'fair' && palette?.secondaryText) || undefined}
        className={classes.timerStayButton}
      >
        {formatText(stayHereButtonText)}
      </Button>
    );
  };

  return (
    <>
      <Backdrop onEl={onEl} sx={props.style} open>
        <Grid className={classes.timerOverlayContainer}>
          <MuiBox className={classes.timerOverlayHeaderBox}>
            <Typography fontSize={72} fontWeight={600} lineHeight={1.5}>
              {formatText(header)}
            </Typography>
          </MuiBox>
          <Box flexOverride="none" margin="0 0 7">
            <Typography variant="h5">{formatText(subheader)}</Typography>
          </Box>
          {/* Progress timer to show remaining time */}
          <Progress time={time} startTime={startTime} />
          {/* Restart or Stay Buttons */}
          <AbsoluteBox bottom="-45px">
            <Stack>
              {resetButton()}
              {stayHereButton()}
            </Stack>
          </AbsoluteBox>
        </Grid>
      </Backdrop>
    </>
  );
};
